import type { Language } from "@prismicio/editor-fields"
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogHeader,
  Separator,
  Text,
  Tooltip
} from "@prismicio/editor-ui"
import { useState } from "react"

import { LocaleSelection } from "../../../app/components/Locales/LocaleSelection"
import { useRole } from "../../../app/services/user"
import { useDocument } from "../../DocumentProvider"
import { useLocales } from "./useLocales"

export function Locales() {
  const { type } = useDocument()

  const { onLocaleChange, isEnabled, locale, langs, onCopyToLocale } =
    useLocales()

  const [dialogOpen, setDialogOpen] = useState(false)

  function onOpenChange(open: boolean) {
    if (!open) setDialogOpen(open)
  }

  const { isContributor } = useRole(locale)
  const canCopyToLocale = !isContributor

  const copyButtonDisabled = type === "new" || !canCopyToLocale

  if (!locale) return null

  if (langs.length < 2) return null

  return (
    <Box flexShrink={0} flexDirection="column">
      <Box
        padding={{ bottom: 6 }}
        justifyContent="space-between"
        alignItems="center"
      >
        <Text variant="smallBold" color="grey9" align="end">
          Locales
        </Text>
        <Tooltip
          visible={!canCopyToLocale}
          title={"You can't make changes"}
          content="Your role is read-only. Only users with editing permission can make changes."
        >
          <Button
            invisible
            size="small"
            onClick={() => setDialogOpen(true)}
            disabled={copyButtonDisabled}
            underline
          >
            Copy to another locale
          </Button>
        </Tooltip>
      </Box>
      <CopyToLocaleDialog
        open={dialogOpen}
        onOpenChange={onOpenChange}
        locale={locale}
        langs={langs}
        onCopyToLocale={onCopyToLocale}
      />
      <LocaleSelection
        locale={locale}
        isEnabled={isEnabled}
        onLocaleChange={onLocaleChange}
        langs={langs}
      />

      <Separator sx={{ marginBlock: 24 }} />
    </Box>
  )
}

interface CopyToLocaleProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  locale: string
  onCopyToLocale: (localeId: string) => void
  langs: Language[]
}

function CopyToLocaleDialog(props: CopyToLocaleProps) {
  const { open, onOpenChange, locale, onCopyToLocale, langs } = props

  const [dialogLocale, setDialogLocale] = useState<string>(locale)

  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="small">
      <DialogHeader icon="contentCopy" title="Copy to another locale" />
      <DialogContent>
        <Box
          padding={{ inline: 16, top: 16, bottom: 8 }}
          flexDirection="column"
        >
          <LocaleSelection
            locale={dialogLocale}
            onLocaleChange={(localeId) => setDialogLocale(localeId)}
            langs={langs}
          />
        </Box>
        <DialogActions
          ok={{
            text: "Copy all content",
            onClick: () => onCopyToLocale(dialogLocale)
          }}
          cancel={{ text: "Cancel" }}
        />
      </DialogContent>
    </Dialog>
  )
}
